import type { FC } from 'react';
import { DownOutlined } from '@ant-design/icons';
import { Button, Dropdown, Input, Modal, Space, Tooltip } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PRIVATE_PATH } from '@/constants';
import { useActions } from './hooks/useActions';
import { ActionsProps } from './Actions.types';
import { StyledButton } from './Actions.styles';
import { MediaPlanForm } from '@/components';

function parseBudgetInput(s: string): number | null {
  const cleaned = s.replace(/[£$€\s]/g, '').replace(/,/g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Display budgets as £12,500 for the scenarios modal, keeping any pence:
 *  £12,500.50 stays £12,500.50 rather than becoming £12,501. */
function formatBudgetDisplay(s: string): string {
  const n = parseBudgetInput(s);
  if (n == null) return s;
  return `£${n.toLocaleString('en-GB', { maximumFractionDigits: 2 })}`;
}

const DEFAULT_BUDGET_INPUTS = ['£50,000', '£100,000', '£150,000'];

const Actions: FC<ActionsProps> = ({ id, mediaPlan }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isScenarioOpen, setIsScenarioOpen] = useState(false);
  const [budgetInputs, setBudgetInputs] = useState<string[]>(DEFAULT_BUDGET_INPUTS);

  const {
    menuItems,
    isEditMediaPlanFormOpen,
    closeEditMediaPlanForm,
    downloadIOPdf,
    canUploadPdf,
    isPdfLoading,
    downloadExcelCurrent,
    downloadExcelScenarios,
    canDownloadExcel,
    isExcelLoading
  } = useActions({
    id,
    mediaPlan
  });

  const parsedBudgets = useMemo(() => {
    const nums = budgetInputs.map(parseBudgetInput).filter((n): n is number => n != null);
    return Array.from(new Set(nums));
  }, [budgetInputs]);

  const downloadItems = [
    ...(canDownloadExcel && downloadExcelCurrent
      ? [
          {
            key: 'excel-current',
            label: 'Media Plan (.xlsx)',
            onClick: () => downloadExcelCurrent()
          },
          {
            key: 'excel-scenarios',
            label: 'Media Plan Options (.xlsx)',
            onClick: () => setIsScenarioOpen(true)
          }
        ]
      : []),
    {
      key: 'io-pdf',
      label: 'IO (.pdf)',
      disabled: !canUploadPdf,
      onClick: () => downloadIOPdf?.()
    }
  ];

  return (
    <Space>
      <MediaPlanForm mediaPlan={mediaPlan} isOpen={isEditMediaPlanFormOpen} close={closeEditMediaPlanForm} />
      <Dropdown menu={{ items: menuItems }}>
        <StyledButton size="large">
          {t('more')}
          <DownOutlined style={{ fontSize: '9px' }} />
        </StyledButton>
      </Dropdown>

      <Tooltip title={!canUploadPdf ? t('pdfDownloadDisabledHint') : undefined}>
        <Dropdown menu={{ items: downloadItems }}>
          <StyledButton size="large" disabled={isPdfLoading || isExcelLoading}>
            Download
            <DownOutlined style={{ fontSize: '9px' }} />
          </StyledButton>
        </Dropdown>
      </Tooltip>

      <Tooltip title={!canUploadPdf ? t('pdfDownloadDisabledHint') : undefined}>
        <StyledButton
          size="large"
          type="primary"
          disabled={!canUploadPdf}
          onClick={() => navigate(`${PRIVATE_PATH.MEDIA_PLAN}/${id}/raise-io`)}
        >
          Raise IO
        </StyledButton>
      </Tooltip>

      <Modal
        className="excel-scenarios-modal"
        title="Download Excel (scenarios)"
        open={isScenarioOpen}
        onCancel={() => setIsScenarioOpen(false)}
        afterOpenChange={(open) => {
          if (open) {
            setBudgetInputs((prev) => prev.map(formatBudgetDisplay));
          }
        }}
        okText="Download"
        okButtonProps={{
          loading: isExcelLoading,
          disabled: parsedBudgets.length < 3
        }}
        onOk={async () => {
          await downloadExcelScenarios?.(parsedBudgets);
          setIsScenarioOpen(false);
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={8}>
          {budgetInputs.map((value, idx) => (
            <Input
              key={idx}
              value={value}
              placeholder="e.g. £50,000"
              onChange={(e) => {
                const next = [...budgetInputs];
                next[idx] = e.target.value;
                setBudgetInputs(next);
              }}
              onBlur={() => {
                setBudgetInputs((prev) => {
                  const next = [...prev];
                  next[idx] = formatBudgetDisplay(next[idx] ?? '');
                  return next;
                });
              }}
              addonBefore={`Budget ${idx + 1}`}
            />
          ))}
          <Space>
            <Button type="link" onClick={() => setBudgetInputs((prev) => [...prev, ''])}>
              + Add another budget
            </Button>
            <Button
              type="link"
              disabled={budgetInputs.length <= 3}
              onClick={() => setBudgetInputs((prev) => prev.slice(0, -1))}
            >
              Remove last
            </Button>
          </Space>
          {parsedBudgets.length < 3 && (
            <div style={{ color: 'var(--ant-color-error)' }}>Please enter at least 3 valid budgets.</div>
          )}
        </Space>
      </Modal>

    </Space>
  );
};

export default Actions;
